import { Link, useSearchParams } from 'react-router-dom';

interface PaginationProps {
  current: number;
  total: number;
}

const buildPages = (current: number, total: number): (number | 'dots')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | 'dots')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('dots');
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < total - 1) pages.push('dots');
  pages.push(total);
  return pages;
};

export default function Pagination({ current, total }: PaginationProps) {
  const [params] = useSearchParams();

  if (total <= 1) return null;

  const linkFor = (page: number) => {
    const next = new URLSearchParams(params);
    if (page === 1) next.delete('page');
    else next.set('page', String(page));
    const qs = next.toString();
    return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  };

  return (
    <nav className="mt-10 flex flex-wrap items-center justify-center gap-1.5" aria-label="分页">
      {current > 1 && (
        <Link
          to={linkFor(current - 1)}
          className="flex h-9 items-center gap-1 rounded-md border border-background-300/70 px-3 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
        >
          <i className="ri-arrow-left-s-line"></i>
          上一页
        </Link>
      )}
      {buildPages(current, total).map((page, index) =>
        page === 'dots' ? (
          <span key={`dots-${index}`} className="flex h-9 w-6 items-center justify-center text-sm text-foreground-400">
            …
          </span>
        ) : (
          <Link
            key={page}
            to={linkFor(page)}
            aria-current={page === current ? 'page' : undefined}
            className={`flex h-9 min-w-9 items-center justify-center rounded-md px-2.5 font-label text-sm transition-colors ${
              page === current
                ? 'bg-accent-500 font-bold text-foreground-950'
                : 'border border-background-300/70 text-foreground-700 hover:border-accent-400 hover:text-accent-700'
            }`}
          >
            {page}
          </Link>
        ),
      )}
      {current < total && (
        <Link
          to={linkFor(current + 1)}
          className="flex h-9 items-center gap-1 rounded-md border border-background-300/70 px-3 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
        >
          下一页
          <i className="ri-arrow-right-s-line"></i>
        </Link>
      )}
    </nav>
  );
}